import React, {FunctionComponent, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Button,
  Card,
  H4,
  Icon,
  Spinner,
  Tab,
  Tabs,
  TabId,
} from '@blueprintjs/core';

import {IState} from '../../shared/store/reducers';
import {ApiStatus} from '../../shared/models';
import {isAdmin} from '../../shared/utils/auth';
import InviteUserButton from '../../features/admin/users/InviteUserButton';
import {loadWorkspaceStats} from '../../shared/store/actions/workspaceActions';
import {reportIcon} from '../../shared/utils/custom-icons';
import config from '../../config';

import './GettingStarted.scss';

interface TabIndicatorProps {
  completed: boolean;
  text: string;
}

interface GettingStartedProps {}

const TabIndicator: FunctionComponent<TabIndicatorProps> = ({
  completed,
  text,
}: TabIndicatorProps) => {
  return (
    <div className="getting-started__tab-indicator">
      <Icon
        icon={completed ? 'tick-circle' : 'circle'}
        intent={completed ? 'primary' : 'none'}
      />
      {text}
    </div>
  );
};

const defaultChecklistItems = [
  {
    id: 1,
    text: 'Connect to your Data',
    completed: false,
  },
  {
    id: 2,
    text: 'Schedule your first Report',
    completed: false,
  },
  {id: 3, text: 'Invite your Team', completed: false},
];

const GettingStarted: FunctionComponent<
  GettingStartedProps
> = ({}: GettingStartedProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state: IState) => state.users?.user);
  const stats = useSelector((state: IState) => state.workspaces.stats);
  const loadingStatsStatus = useSelector(
    (state: IState) => state.workspaces.loadingStatsStatus
  );
  const [onboarded, setOnboarded] = useState<boolean>(false);
  const [checkListItems, setCheckListItems] = useState(defaultChecklistItems);
  const [skipIntro, setSkipIntro] = useState<boolean>(false);
  const [isStatsLoaded, setIsStatsLoaded] = useState<boolean>(false);
  const [currentTabId, setCurrentTabId] = useState<TabId>('1');

  const customerName = config.app.customer_name;

  useEffect(() => {
    dispatch(loadWorkspaceStats());
  }, []);

  useEffect(() => {
    // we check isDismissedGetttingStarted for backwards compatibility
    const skipIntroStatus =
      localStorage.getItem('skipIntro') ||
      localStorage.getItem('isDismissedGetttingStarted');
    if (skipIntroStatus === 'true') {
      setSkipIntro(true);
    }

    // we check setDidBookOnboarding for backwards compatibility
    const onboardedStatus =
      localStorage.getItem('onboarded') ||
      localStorage.getItem('setDidBookOnboarding');
    if (onboardedStatus === 'true') {
      setOnboarded(true);
    }
  }, []);

  // update checklist items based on current state
  useEffect(() => {
    if (loadingStatsStatus === ApiStatus.LOADED) {
      setIsStatsLoaded(true);

      const isDataConnected =
        stats.databases_count > 0 || stats.tableau_integrations_count > 0;
      const isWorkflowScheduled = stats.workflows_count > 0;
      const isReportScheduled = stats.reports_count > 0;
      const isTeamInvited = stats.users_count > 1;

      setCheckListItems(
        checkListItems.map(item => {
          switch (item.id) {
            case 1:
              return {...item, completed: isDataConnected};
            case 2:
              return {...item, completed: isWorkflowScheduled || isReportScheduled};
            case 3:
              return {...item, completed: isTeamInvited};
            default:
              return item;
          }
        })
      );
      // set upNext whenever loadingStats is loaded
      setCurrentTabId(getNextUp()?.toString() as TabId);
    }
  }, [loadingStatsStatus]);

  const handleDismiss = () => {
    // Save the dismiss state to localStorage
    //  @TODO this should include the workspace_id
    localStorage.setItem('skipIntro', 'true');
    setSkipIntro(true);
  };

  useEffect(() => {
    if (onboarded) {
      setCheckListItems(
        checkListItems.map(item =>
          item.id === 5 ? {...item, completed: true} : item
        )
      );
    }
  }, [onboarded]);

  const getNextUp = () => {
    if (checkListItems.find(item => !item.completed)?.id) {
      return checkListItems.find(item => !item.completed)?.id;
    }
    return 3;
  };

  useEffect(() => {
    setCurrentTabId(getNextUp()?.toString() as TabId);
  }, []);

  const onClickBookDemo = () => {
    // Save the dismiss state to localStorage
    //  @TODO this should include the workspace_id
    localStorage.setItem('onboarded', 'true');
    setOnboarded(true);
  };

  const goToReport = () => {
    history.push('/r/new');
  }

  const getPanelFor = (panelNumber: number) => {
    switch (panelNumber) {
      case 1:
        return (
          <div className="getting-started__panel">
            <H4>Connect to Data</H4>
            <p>
              Connect to a SQL database or add a Tableau integration to get your
              data into {customerName}. Once connected you can use your data in
              your Reports and collaborate with others to build data
              workflows, like attaching SQL results or Tableau content to
              Emails.
            </p>
            <a href="/d/n">
              <Button intent="primary" icon="database">
                Connect to Data
              </Button>
            </a>
          </div>
        );
      case 2:
        return (
          <div className="getting-started__panel">
            <H4>Schedule Your First Report</H4>
            <p>
              Schedule your first Report to send SQL results or Tableau exports
              via email or Slack.
            </p>
            <Button
              intent="primary"
              icon={reportIcon({})}
              onClick={goToReport}
            >
              Create a Report
            </Button>
          </div>
        );
      case 3:
        return (
          <div className="getting-started__panel">
            <H4>Invite Your Team</H4>
            <p>
              Start collaborating on your data workflows with all your
              colleagues. Invite them as Admins, Regular Users, or View Only.
            </p>
            <InviteUserButton />
          </div>
        );
      case 5:
        return (
          <div className="getting-started__panel">
            <H4>Book an Onboarding Session</H4>
            <p>
              Let's get you started with ${customerName}. Book a free onboarding
              session with one of our experts to get you up and running.
            </p>
            <a href="/book-a-demo">
              <Button
                intent="primary"
                icon="presentation"
                onClick={() => onClickBookDemo()}
              >
                Book Onboarding Session
              </Button>
            </a>
          </div>
        );
      default:
        return undefined;
    }
  };

  const handleOnChange = (newTabId: TabId, prevTabId: TabId) => {
    setCurrentTabId(newTabId);
  };

  if (skipIntro || !isAdmin(currentUser)) {
    return null;
  }

  return (
    <Card className="getting-started">
      <div className="getting-started__header">
        <H4>
          <Icon icon="clean" />
          Getting Started
        </H4>
        <Button
          className="getting-started__dismiss-button"
          minimal
          icon="cross"
          onClick={handleDismiss}
        />
      </div>
      {isStatsLoaded ? (
        <Tabs
          id="checklist"
          vertical={true}
          onChange={handleOnChange}
          selectedTabId={currentTabId}
        >
          {checkListItems.map(item => (
            <Tab
              key={item.id}
              id={item.id.toString()}
              title={
                <TabIndicator completed={item.completed} text={item.text} />
              }
              panel={getPanelFor(item.id)}
            />
          ))}
        </Tabs>
      ) : (
        <Spinner />
      )}
    </Card>
  );
};

export default GettingStarted;
